export const teams = {
  human: require("src/assets/images/human_icon.webp"),
  zombie: require("src/assets/images/zombie_icon.webp"),
}

/* The sprite each player is wearing on the map, keyed by the cosmetic id the server publishes
   (`GET /game-config/` -> `economy.cosmetics`). The ids are the contract; these files are the art
   behind them, and swapping one for a commissioned drawing later is an edit here and nowhere else.

   All eighteen are cut from three generated sheets by `scripts/make_icons.py` (2026-09-07),
   which is checked in beside its sources: the six free looks a side are one drawing re-inked,
   the three paid looks a side are each their own drawing off the same sheet. Replacing one is
   replacing a sheet and running the script. */
/* Reactions that are a picture rather than an emoji, keyed by the same id the socket carries.
   The wire never names a file — which one an id means is this client's business, exactly as which
   emoji it means always was. */
export const reactionImages = {
  react_gigachad: require("src/assets/images/reactions/gigachad.png"),
}

/* The three effects the round's playback draws (requirement 36, S4), four frames each, in order:
   `clash` where two forces meet (a burst with crossed strokes, no blood, no side colour), `blast`
   where a missile lands (a flash, then a dark cloud), `tunnel` for the network's mouth (a purple
   swirl, played forward for an entry and backwards for an exit). Cut from the Gemini sheets in
   `scripts/sprite_sources/` by `scripts/make_unit_sprites.py`; the canvas picks the frame by the
   cue's progress. */
export const effectSheets = {
  clash: [
    require("src/assets/images/effects/clash_0.webp"),
    require("src/assets/images/effects/clash_1.webp"),
    require("src/assets/images/effects/clash_2.webp"),
    require("src/assets/images/effects/clash_3.webp"),
  ],
  blast: [
    require("src/assets/images/effects/blast_0.webp"),
    require("src/assets/images/effects/blast_1.webp"),
    require("src/assets/images/effects/blast_2.webp"),
    require("src/assets/images/effects/blast_3.webp"),
  ],
  tunnel: [
    require("src/assets/images/effects/tunnel_0.webp"),
    require("src/assets/images/effects/tunnel_1.webp"),
    require("src/assets/images/effects/tunnel_2.webp"),
    require("src/assets/images/effects/tunnel_3.webp"),
  ],
}

export const unitIcons = {
  // The six free player colours a side (owner's ask, 2026-09-04): the sheet's blue soldier and
  // the sheet's walker, re-inked five times by `scripts/make_icons.py`, plus that walker's shirt
  // dropped to black for the zombie default. Since 2026-09-08 rose and violet are inked onto a
  // woman instead — same ids, same colours, a different drawing underneath.
  human_default: require("src/assets/images/human_icon.webp"),
  human_amber: require("src/assets/images/human_amber_icon.webp"),
  human_violet: require("src/assets/images/human_violet_icon.webp"),
  human_teal: require("src/assets/images/human_teal_icon.webp"),
  human_rose: require("src/assets/images/human_rose_icon.webp"),
  human_lime: require("src/assets/images/human_lime_icon.webp"),
  zombie_amber: require("src/assets/images/zombie_amber_icon.webp"),
  zombie_violet: require("src/assets/images/zombie_violet_icon.webp"),
  zombie_teal: require("src/assets/images/zombie_teal_icon.webp"),
  zombie_rose: require("src/assets/images/zombie_rose_icon.webp"),
  zombie_lime: require("src/assets/images/zombie_lime_icon.webp"),
  // The paid looks, half of them women (owner, 2026-09-08). The three that were here are
  // untouched; the three beside them were drawn in the same Gemini conversation.
  human_helmet: require("src/assets/images/human_helmet_icon.webp"),
  human_grenade: require("src/assets/images/human_grenade_icon.webp"),
  human_gasmask: require("src/assets/images/human_gasmask_icon.webp"),
  human_helmet_f: require("src/assets/images/human_helmet_f_icon.webp"),
  human_grenade_f: require("src/assets/images/human_grenade_f_icon.webp"),
  human_gasmask_f: require("src/assets/images/human_gasmask_f_icon.webp"),
  // Humanity's epic (owner, 2026-10-09), the radiation trefoil, partner of the horde's grave.
  human_nuclear: require("src/assets/images/human_nuclear_icon.webp"),
  zombie_default: require("src/assets/images/zombie_icon.webp"),
  zombie_skull: require("src/assets/images/zombie_skull_icon.webp"),
  zombie_jaws: require("src/assets/images/zombie_jaws_icon.webp"),
  zombie_grave: require("src/assets/images/zombie_grave_icon.webp"),
  zombie_screamer: require("src/assets/images/zombie_screamer_icon.webp"),
  zombie_bride: require("src/assets/images/zombie_bride_icon.webp"),
  zombie_nurse: require("src/assets/images/zombie_nurse_icon.webp"),
}
/* The player's picture (owner, 2026-10-08), keyed by the cosmetic id the server publishes
   (`economy.avatars`). One per account, not per side: it is what the ranking, the hall of fame,
   the header and the profile draw, where a person is shown rather than a seat. Square portraits
   in the unit plates' style, generated in Gemini and ChatGPT; the sources and the script that
   makes these files are `scripts/avatar_sources/` and `scripts/make_avatars.py`. */
export const avatars = {
  avatar_soldier: require("src/assets/images/avatars/avatar_soldier.webp"),
  avatar_soldier_f: require("src/assets/images/avatars/avatar_soldier_f.webp"),
  avatar_zombie: require("src/assets/images/avatars/avatar_zombie.webp"),
  avatar_zombie_f: require("src/assets/images/avatars/avatar_zombie_f.webp"),
  avatar_hazmat: require("src/assets/images/avatars/avatar_hazmat.webp"),
  avatar_zombie_king: require("src/assets/images/avatars/avatar_zombie_king.webp"),
  avatar_hunter: require("src/assets/images/avatars/avatar_hunter.webp"),
  // The beta's one-credit picture: the zombie as a sprite on a flickering old television, animated
  // WebP (`scripts/make_beta_avatar.py`).
  avatar_beta: require("src/assets/images/avatars/avatar_beta.webp"),
  // The epic ones move: animated WebP, which an <img> plays wherever it is drawn
  // (`scripts/make_epic_avatars.py`).
  avatar_necromancer: require("src/assets/images/avatars/avatar_necromancer.webp"),
  avatar_stormbringer: require("src/assets/images/avatars/avatar_stormbringer.webp"),
  avatar_inferno: require("src/assets/images/avatars/avatar_inferno.webp"),
  // Epic and never sold: granted from the admin (`economy/config.py`, `on_sale=False`).
  avatar_explorer: require("src/assets/images/avatars/avatar_explorer.webp"),
}
// A class wears the portrait of the unit only it can field — the same face the garrison and the
// roster show — so the card and the army match (owner, 2026-09-06: the warrior wore the soldier
// and the zombies wore plates nothing on the map ever draws). The two free classes, which unlock
// nothing, wear their side's basic body.
// Named rather than pointed at, so the shop can say *which* unit a card is a picture of — its
// showcase is that unit's, not a second one about the class (owner, 2026-09-06). `races` below is
// derived, so the two cannot disagree.
export const raceUnits = {
  clumsy: "zombie",
  assassin: "walker",
  infestor: "ambusher",
  intelligent: "dog",
  chicken: "worker",
  warrior: "elite",
  economist: "medic",
  technologist: "helicopter",
}
export const buildings = {
  wall: require("src/assets/images/buildings/wall.webp"),
  command_center: require("src/assets/images/buildings/command_center.webp"),
  cultivation_center: require("src/assets/images/buildings/cultivation_center.webp"),
  academy: require("src/assets/images/buildings/academy.webp"),
  missile_silo: require("src/assets/images/buildings/missile_silo.webp"),
  hatchery: require("src/assets/images/buildings/hatchery.webp"),
  putrefactor: require("src/assets/images/buildings/putrefactor.webp"),
  evolution_chamber: require("src/assets/images/buildings/evolution_chamber.webp"),
  tunnel: require("src/assets/images/buildings/tunnel.webp"),
}
const units = {
  worker: require("src/assets/images/units/human_worker.webp"),
  soldier: require("src/assets/images/units/human_soldier.webp"),
  ranger: require("src/assets/images/units/human_ranger.webp"),
  tank: require("src/assets/images/units/human_tank.webp"),
  elite: require("src/assets/images/units/human_elite.webp"),
  medic: require("src/assets/images/units/human_medic.webp"),
  helicopter: require("src/assets/images/units/human_helicopter.webp"),
  zombie: require("src/assets/images/units/zombie_zombie.webp"),
  cursed: require("src/assets/images/units/zombie_cursed.webp"),
  destructor: require("src/assets/images/units/zombie_destructor.webp"),
  titan: require("src/assets/images/units/zombie_titan.webp"),
  walker: require("src/assets/images/units/zombie_walker.webp"),
  ambusher: require("src/assets/images/units/zombie_ambusher.webp"),
  dog: require("src/assets/images/units/zombie_dog.webp"),
}
export { units }
export const races = Object.fromEntries(Object.entries(raceUnits).map(([race, unit]) => [race, units[unit]]))
